/**
 * Room Type Color Coding System
 * Provides consistent color schemes for different room types across the application
 * Supports both auto-assignment and custom colors per hotel
 */

// Expanded color palette - 20 distinct colors
export const AVAILABLE_COLORS = [
  { id: 'purple', name: 'Purple', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', gradient: 'from-purple-500 to-purple-600', badge: 'bg-purple-500', icon: '👑' },
  { id: 'blue', name: 'Blue', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', gradient: 'from-blue-500 to-blue-600', badge: 'bg-blue-500', icon: '🛏️' },
  { id: 'amber', name: 'Amber', bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', gradient: 'from-amber-500 to-orange-500', badge: 'bg-amber-500', icon: '✨' },
  { id: 'rose', name: 'Rose', bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300', gradient: 'from-rose-500 to-pink-600', badge: 'bg-rose-500', icon: '🏆' },
  { id: 'green', name: 'Green', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', gradient: 'from-green-500 to-emerald-600', badge: 'bg-green-500', icon: '🌿' },
  { id: 'cyan', name: 'Cyan', bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300', gradient: 'from-cyan-500 to-teal-600', badge: 'bg-cyan-500', icon: '💎' },
  { id: 'indigo', name: 'Indigo', bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300', gradient: 'from-indigo-500 to-indigo-600', badge: 'bg-indigo-500', icon: '🔵' },
  { id: 'pink', name: 'Pink', bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300', gradient: 'from-pink-500 to-pink-600', badge: 'bg-pink-500', icon: '🌸' },
  { id: 'violet', name: 'Violet', bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300', gradient: 'from-violet-500 to-purple-600', badge: 'bg-violet-500', icon: '🌟' },
  { id: 'teal', name: 'Teal', bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300', gradient: 'from-teal-500 to-cyan-600', badge: 'bg-teal-500', icon: '🌊' },
  { id: 'orange', name: 'Orange', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', gradient: 'from-orange-500 to-amber-600', badge: 'bg-orange-500', icon: '🔶' },
  { id: 'lime', name: 'Lime', bg: 'bg-lime-100', text: 'text-lime-700', border: 'border-lime-300', gradient: 'from-lime-500 to-green-600', badge: 'bg-lime-500', icon: '🍋' },
  { id: 'emerald', name: 'Emerald', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', gradient: 'from-emerald-500 to-teal-600', badge: 'bg-emerald-500', icon: '💚' },
  { id: 'sky', name: 'Sky', bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-sky-300', gradient: 'from-sky-500 to-blue-600', badge: 'bg-sky-500', icon: '☁️' },
  { id: 'fuchsia', name: 'Fuchsia', bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', border: 'border-fuchsia-300', gradient: 'from-fuchsia-500 to-pink-600', badge: 'bg-fuchsia-500', icon: '💜' },
  { id: 'yellow', name: 'Yellow', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', gradient: 'from-yellow-500 to-amber-600', badge: 'bg-yellow-500', icon: '⭐' },
  { id: 'red', name: 'Red', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', gradient: 'from-red-500 to-rose-600', badge: 'bg-red-500', icon: '🔴' },
  { id: 'slate', name: 'Slate', bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300', gradient: 'from-slate-500 to-gray-600', badge: 'bg-slate-500', icon: '⚪' },
  { id: 'zinc', name: 'Zinc', bg: 'bg-zinc-100', text: 'text-zinc-700', border: 'border-zinc-300', gradient: 'from-zinc-500 to-slate-600', badge: 'bg-zinc-500', icon: '⚫' },
  { id: 'stone', name: 'Stone', bg: 'bg-stone-100', text: 'text-stone-700', border: 'border-stone-300', gradient: 'from-stone-500 to-zinc-600', badge: 'bg-stone-500', icon: '🪨' }
];

// Default room type to color mappings (fallback)
const DEFAULT_ROOM_TYPE_COLORS = {
  'deluxe': 'purple',
  'suite': 'amber',
  'presidential': 'rose',
  'executive': 'indigo',
  'standard': 'blue',
  'single': 'cyan',
  'king': 'violet',
  'queen': 'pink',
  'family': 'green',
  'twin': 'teal',
  'double': 'sky',
  'penthouse': 'fuchsia'
};

// Get color by ID
export const getColorById = (colorId) => {
  return AVAILABLE_COLORS.find(c => c.id === colorId) || AVAILABLE_COLORS[0];
};

// Auto-assign next available color (not already used)
export const getNextAvailableColor = (usedColorIds = []) => {
  const available = AVAILABLE_COLORS.filter(c => !usedColorIds.includes(c.id));
  return available.length > 0 ? available[0] : AVAILABLE_COLORS[0];
};

// Get room type color (with custom colors support)
export const getRoomTypeColor = (roomType, customColors = {}) => {
  if (!roomType) {
    return getColorById('gray');
  }

  // 1. Check if custom color is defined for this exact room type
  if (customColors[roomType]) {
    return getColorById(customColors[roomType]);
  }

  // 2. Check default mappings by keyword
  const type = roomType.toLowerCase();
  for (const [keyword, colorId] of Object.entries(DEFAULT_ROOM_TYPE_COLORS)) {
    if (type.includes(keyword)) {
      return getColorById(colorId);
    }
  }

  // 3. Auto-assign based on hash (consistent for same name)
  const hashCode = roomType.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const colorIndex = hashCode % AVAILABLE_COLORS.length;
  return AVAILABLE_COLORS[colorIndex];
};

// Get room type badge component
export const getRoomTypeBadge = (roomType, customColors = {}) => {
  const colors = getRoomTypeColor(roomType, customColors);
  return `${colors.badge} text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm`;
};
